import type { ReactNode } from 'react';
import { Alert, Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ReportSectionShellProps {
  title: string;
  testId: string;
  children: ReactNode;
}

/** Card with the section title, shared by the data, empty, loading and error states. */
export function ReportSectionShell({ title, testId, children }: ReportSectionShellProps) {
  return (
    <Grid size={{ xs: 12 }}>
      <Paper
        elevation={0}
        data-testid={testId}
        sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        {children}
      </Paper>
    </Grid>
  );
}

/**
 * One compact line for a period without data, in place of zero cards and empty
 * charts. A section that is loading or failed is NOT empty — see
 * `ReportSectionStatus`.
 */
export function CompactNoData({ title, testId }: { title: string; testId: string }) {
  const { t } = useTranslation('reports');
  return (
    <ReportSectionShell title={title} testId={testId}>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid={`${testId}-no-data`}
      >
        {t('noDataThisPeriod')}
      </Typography>
    </ReportSectionShell>
  );
}

interface ReportSectionStatusProps {
  title: string;
  testId: string;
  isLoading: boolean;
  isError: boolean;
}

/** Loading or error placeholder for a section whose query has no data yet. */
export function ReportSectionStatus({ title, testId, isLoading, isError }: ReportSectionStatusProps) {
  const { t } = useTranslation('reports');
  return (
    <ReportSectionShell title={title} testId={testId}>
      {isError ? (
        <Alert severity="error" data-testid={`${testId}-error`}>
          {t('sectionLoadError')}
        </Alert>
      ) : isLoading ? (
        <Box
          role="status"
          aria-label={t('sectionLoading')}
          data-testid={`${testId}-loading`}
          sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : null}
    </ReportSectionShell>
  );
}
